"use client";

import type {
  ServiceConfigurationValue,
  ServiceTemplateFieldDefinition,
} from "@louez/types";
import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@louez/ui";

type ConfiguredServiceFieldProps = {
  compact?: boolean;
  error?: string;
  field: ServiceTemplateFieldDefinition;
  onBlur: () => void;
  onChange: (value: ServiceConfigurationValue) => void;
  value: ServiceConfigurationValue;
};

export const ConfiguredServiceField = ({
  compact = false,
  error,
  field,
  onBlur,
  onChange,
  value,
}: ConfiguredServiceFieldProps) => {
  const inputId = `service-field-${field.key}`;
  const errorId = `${inputId}-error`;

  if (field.type === "checkbox") {
    return (
      <div className="space-y-1">
        <label className="border-input bg-background flex min-h-10 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium">
          <Checkbox
            id={inputId}
            aria-describedby={error ? errorId : undefined}
            aria-invalid={Boolean(error)}
            checked={value === true}
            onBlur={onBlur}
            onCheckedChange={(checked) => onChange(checked === true)}
          />
          <span>{field.label}</span>
        </label>
        {!compact && field.description && (
          <p className="text-muted-foreground text-xs">{field.description}</p>
        )}
        {error && (
          <p id={errorId} className="text-destructive text-xs">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId} className="text-sm">
        {field.label}
        {field.required ? " *" : ""}
        {field.description ? (
          <span className="text-muted-foreground font-normal">
            {" "}
            ({field.description})
          </span>
        ) : null}
      </Label>
      {field.type === "select" ? (
        <Select
          items={field.options}
          value={typeof value === "string" && value !== "" ? value : null}
          onValueChange={(nextValue) => {
            if (nextValue !== null) onChange(nextValue);
          }}
        >
          <SelectTrigger
            id={inputId}
            aria-describedby={error ? errorId : undefined}
            aria-invalid={Boolean(error)}
            onBlur={onBlur}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={inputId}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={Boolean(error)}
          required={field.required}
          type={field.type === "number" ? "number" : "text"}
          value={
            typeof value === "string" ||
            (typeof value === "number" && Number.isFinite(value))
              ? value
              : ""
          }
          onBlur={onBlur}
          onChange={(event) =>
            onChange(
              field.type === "number"
                ? event.target.value === ""
                  ? Number.NaN
                  : Number(event.target.value)
                : event.target.value,
            )
          }
        />
      )}
      {error && (
        <p id={errorId} className="text-destructive text-xs">
          {error}
        </p>
      )}
    </div>
  );
};
